import { useState, useEffect } from "react";
import axios from "../config/axios";
import { useAuth } from "../contexts/AuthContext";

/**
 * Custom hook for managing subscription state and limits
 */
export const useSubscription = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch subscription data
  const fetchSubscription = async () => {
    if (!user) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = await user.getIdToken();
      const response = await axios.get("/api/subscription/status", {
        headers: { Authorization: `Bearer ${token}` },
      });

      // The API returns { subscription: {...}, usage: {...}, trial: {...} }
      // We need the subscription object
      console.log("Subscription response:", response.data);
      setSubscription(response.data.subscription);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch subscription:", err);
      setError(err.message);
      // Set default free plan on error
      setSubscription({
        status: "free",
        plan: {
          slug: "free",
          name: "Free",
          limits: {
            analyses_total: 1,
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when user changes
  useEffect(() => {
    fetchSubscription();
  }, [user]);

  // Check if user has a paid plan
  const hasPaidPlan = () => {
    if (!subscription) {
      console.log("hasPaidPlan: No subscription data");
      return false;
    }

    const isPaid =
      subscription.plan?.slug !== "free" &&
      (subscription.status === "active" || subscription.status === "trialing");

    console.log("hasPaidPlan check:", {
      planSlug: subscription.plan?.slug,
      status: subscription.status,
      isPaid,
    });

    return isPaid;
  };

  // Check if user can access a specific feature
  const canAccessFeature = (featureName) => {
    if (!subscription) return false;

    // Paid plans can access all features
    if (hasPaidPlan()) return true;

    // Free plan - only Settings and CRO Analyzer
    const freeAllowedFeatures = ["settings", "cro_analyzer", "subscription"];
    return freeAllowedFeatures.includes(featureName);
  };

  // Check if user can create a new analyzer
  const canCreateAnalyzer = async () => {
    if (!subscription)
      return { allowed: false, reason: "No subscription data" };

    // Paid plans have unlimited analyzers
    if (hasPaidPlan()) {
      return { allowed: true };
    }

    // Free plan - check limit
    try {
      const token = await user.getIdToken();
      const response = await axios.get("/api/cro/analyses", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const analyses = response.data.analyses || [];
      const limit = subscription.plan?.limits?.analyses_total || 1;

      if (analyses.length >= limit) {
        return {
          allowed: false,
          reason: "limit_reached",
          limit,
          current: analyses.length,
        };
      }

      return { allowed: true };
    } catch (err) {
      console.error("Failed to check analyzer limit:", err);
      return { allowed: false, reason: "error" };
    }
  };

  // Get current plan slug
  const getPlanSlug = () => {
    return subscription?.plan?.slug || "free";
  };

  // Get plan display name
  const getPlanName = () => {
    return subscription?.plan?.name || "Free";
  };

  // Check if user is on free plan
  const isFreePlan = () => {
    return getPlanSlug() === "free";
  };

  return {
    subscription,
    loading,
    error,
    hasPaidPlan,
    canAccessFeature,
    canCreateAnalyzer,
    getPlanSlug,
    getPlanName,
    isFreePlan,
    refetch: fetchSubscription,
  };
};
